import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import arrowRightWhite from '@sg/assests/svg/arrow-right-white.svg';
import { Button, Segment, DatePicker, notification } from 'tea-component';
import moment from 'moment';
import TableForm from '@sg/components/TableForm';
import DatePickerWrapper from '@gp/components/DatePickerWrapper';
import SearchableSelectMultipleInherit from '@sg/components/SelectMultiple/SearchableSelectMultipleInherit';
import Studios from './Studios/index';
import LiveTitles from './LiveTitles/index';
import LiveDownload from './LiveTitles/pages/LiveDownload/index';
import InDevProjects from './InDevProjects/index';
import PortfolioOverview from './PortfolioOverview/index';
import BoardGovernance from './BoardGovernance';
import NoAuthPage from '@sg/components/noAuthPage';
import InDevDownload from './InDevProjects/pages/InDevDownload';
import { usePermissionReady } from '@sg/hooks/usePermissionReady';
import { useIntl, useLocation, history } from 'umi';
import {
  SGBuriedPoint,
  SGBuriedBtnPoint,
  PortfolioOverviewTab,
  StudiosTab,
  LiveTitlesTab,
  InDevProjectsTab,
  BoardGovernanceTab,
  createBtnPoint,
  createPagePoint,
} from '@sg/utils/buriedPoint';
import { getFormattedTabList } from '@sg/_config/permission';
import { ITabListProp } from '../BusinessManagement/type';
import { getDataTime } from '@sg/services';
import { getModulesForPage, GLOBAL_MIN_DATE } from '@sg/_config/dataTimeModules';
import {
  buildLiveTitlesUrl,
  parseLiveTitlesUrl,
  type TLiveView,
} from '@sg/pages/StudioManagement/LiveTitles/utils/liveTitlesUrl';
import { useBoardGovernanceStudios } from './BoardGovernance/hooks/useBoardGovernanceStudios';
import './index.scss';

const { MonthPicker } = DatePicker;

export default function BusinessManagement(props: any) {
  const { formatMessage } = useIntl();
  const { activeSubTab, setActiveSubTab } = props;
  const { ready: permissionReady } = usePermissionReady();
  const [tabList, setTabList] = useState<ITabListProp[]>([]);
  const [maxDataTime, setMaxDataTime] = useState<string | null>(null);

  // State to track each tab's selected month
  const [tabMonthSelections, setTabMonthSelections] = useState<Record<string, any>>({
    portfolio: null,
    studio: null,
    titles: null,
    projects: null,
  });

  // State to track if any tab is in editing mode (disable date picker when editing)
  const [isEditing, setIsEditing] = useState(false);

  // State to track current studio_id for Studios page (used for fetchMaxDataTime)
  const [currentStudioId, setCurrentStudioId] = useState<string | null>(null);

  // State to track current project_id for InDev Projects page (used for fetchMaxDataTime)
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);

  // Tab埋点 config mapping
  const TAB_CONFIGS = {
    portfolio: PortfolioOverviewTab,
    studio: StudiosTab,
    titles: LiveTitlesTab,
    projects: InDevProjectsTab,
    boardGovernance: BoardGovernanceTab,
  };

  // Read activeSegment from URL instead of state
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const activeSegment = searchParams.get('subtitle') || activeSubTab || 'portfolio';
  const { liveView: activeLiveView, gameTitle: activeGameTitle } = useMemo(
    () => parseLiveTitlesUrl(location.search),
    [location.search],
  );

  // Page埋点 - Track page view when sub-tab changes and reset editing state
  useEffect(() => {
    const tabConfig = TAB_CONFIGS[activeSegment as keyof typeof TAB_CONFIGS];
    if (tabConfig) {
      const pagePoint = createPagePoint(tabConfig);
      if (pagePoint) {
        SGBuriedPoint(pagePoint);
      }
    }

    // Reset editing state when switching tabs
    setIsEditing(false);
  }, [activeSegment]);

  // Fetch max data time when active segment changes or studio_id/project_id changes
  useEffect(() => {
    const fetchMaxDataTime = async () => {
      const modules = getModulesForPage(activeSegment);
      if (modules.length === 0) return;

      // For Studios page, wait until studio_id is available before calling API
      if (activeSegment === 'studio' && !currentStudioId) {
        return;
      }

      // For InDev Projects page, wait until project_id is available before calling API
      if (activeSegment === 'projects' && !currentProjectId) {
        return;
      }

      try {
        // Build payload with modules
        const payload: any = { modules };

        // Add studio_id if on Studios page and studio is selected
        if (activeSegment === 'studio' && currentStudioId) {
          payload.studio_id = currentStudioId;
        }

        // Add project_id if on InDev Projects page and project is selected
        if (activeSegment === 'projects' && currentProjectId) {
          payload.project_id = Number(currentProjectId);
        }

        const response = await getDataTime(payload);
        if (response.code === 0 && response.data?.max_datatime) {
          setMaxDataTime(response.data.max_datatime);
        } else {
          // API returned success but no max_datatime
          setMaxDataTime(null);
          // notification.error({
          //   description: 'Unable to retrieve the maximum available date. Please try again later.',
          // });
        }
      } catch (error) {
        console.error('Failed to fetch max data time:', error);
        setMaxDataTime(null);
        notification.error({
          description: 'An error occurred while fetching maximum available date. Please try again later.',
        });
      }
    };

    fetchMaxDataTime();
  }, [activeSegment, currentStudioId, currentProjectId]);

  // Refs to child components → allow parent to call exposed methods
  const inDevRef = useRef<any>(null); // will expose scrollToGRManagement()

  const handleProjectChange = useCallback((projectId: string | null) => {
    setCurrentProjectId(projectId);
  }, []);

  // Board Governance filter state lives in the hook so the selector can sit beside the tab navigation.
  const {
    selectedStudioIds: selectedBoardGovernanceStudioIds,
    studioIds: boardGovernanceStudioIds,
    optionsLoading: boardGovernanceOptionsLoading,
    selectOptions: boardGovernanceSelectOptions,
    selectGroups: boardGovernanceSelectGroups,
    selectButton: boardGovernanceSelectButton,
    handleStudioChange: handleBoardGovernanceStudioChange,
    handleStudioClose: handleBoardGovernanceStudioClose,
  } = useBoardGovernanceStudios({
    activeSegment,
    permissionReady,
    tabList,
  });

  // useMemo ensures optionsSegment updates when tabMonthSelections changes
  const optionsSegment: ITabListProp[] = useMemo(() => {
    // Format the selected month to "YYYY-MM" string for API payload
    const formattedPortfolioMonth = tabMonthSelections.portfolio
      ? moment(tabMonthSelections.portfolio).format('YYYY-MM')
      : null;

    const StudioMonth = tabMonthSelections.studio ? moment(tabMonthSelections.studio).format('YYYY-MM') : null;

    const projectsMonth = tabMonthSelections.projects ? moment(tabMonthSelections.projects).format('YYYY-MM') : null;

    return [
      {
        key: 'studio_portfolio',
        text: formatMessage({ id: 'dbs_subtitle_portfolio_overview' }),
        value: 'portfolio',
        modules: (
          <PortfolioOverview
            key={formattedPortfolioMonth || 'default'}
            selectedMonth={formattedPortfolioMonth}
            maxDataTime={maxDataTime}
            onEditingChange={setIsEditing}
          />
        ),
        trackingKey: 'tabClick',
      },
      {
        key: 'studio_studios',
        text: formatMessage({ id: 'dbs_subtitle_studios' }),
        value: 'studio',
        modules: (
          <Studios
            key={StudioMonth || 'default'}
            selectedMonth={StudioMonth}
            maxDataTime={maxDataTime}
            onEditingChange={setIsEditing}
            onStudioChange={setCurrentStudioId}
          />
        ),
        trackingKey: 'tabClick',
      },
      {
        key: 'studio_liveTitles',
        text: formatMessage({ id: 'dbs_subtitle_live_titles' }),
        value: 'titles',
        modules: <LiveTitles />,
        trackingKey: 'tabClick',
      },
      {
        key: 'studio_inDevProjects',
        text: formatMessage({ id: 'dbs_subtitle_in_dev_projects' }),
        value: 'projects',
        modules: (
          <InDevProjects
            key={projectsMonth || 'default'}
            ref={inDevRef}
            selectedMonth={projectsMonth}
            maxDataTime={maxDataTime || undefined}
            onEditingChange={setIsEditing}
            onProjectChange={handleProjectChange}
          />
        ),
        trackingKey: 'tabClick',
      },
      {
        key: 'studio_boardGovernance',
        text: formatMessage({ id: 'dbs_button_board_governance' }),
        value: 'boardGovernance',
        modules: (
          <BoardGovernance
            selectedStudioIds={selectedBoardGovernanceStudioIds}
            studioOptionsLoading={boardGovernanceOptionsLoading}
            onEditingChange={setIsEditing}
          />
        ),
        trackingKey: 'tabClick',
      },
    ];
  }, [
    tabMonthSelections.portfolio,
    tabMonthSelections.studio,
    tabMonthSelections.projects,
    formatMessage,
    maxDataTime,
    selectedBoardGovernanceStudioIds,
    boardGovernanceOptionsLoading,
  ]); // Re-create when tab filters change

  const handleActiveSegmentChange = (value: string) => {
    // Update URL using history.push to enable RoutePrompt interception
    if (value === 'titles') {
      history.push(buildLiveTitlesUrl({ liveView: 'business', gameTitle: 'all' }));
    } else {
      const currentParams = new URLSearchParams(location.search);
      const params = new URLSearchParams();
      const tab = currentParams.get('tab');
      if (tab) {
        params.set('tab', tab);
      }
      params.set('subtitle', value);
      history.push(`/strategic/home?${params.toString()}`);
    }

    // Still call setActiveSubTab for backward compatibility with parent
    // setActiveSubTab(value);

    // Button埋点 - Track tab click
    const data = tabList.find((item) => item.value === value);
    if (data?.trackingKey) {
      const tabConfig = TAB_CONFIGS[value as keyof typeof TAB_CONFIGS];
      if (tabConfig) {
        const btnPoint = createBtnPoint(tabConfig, data.trackingKey, data.text);
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
      }
    }
  };

  // Handle date selection for the current tab
  const handleDateChange = (date: any) => {
    setTabMonthSelections((prev) => ({
      ...prev,
      [activeSegment]: date,
    }));

    const tabConfig = TAB_CONFIGS[activeSegment as keyof typeof TAB_CONFIGS];
    if (!tabConfig || activeSegment === 'titles') {
      return;
    }

    const selectedValue = date ? moment(date).format('YYYY-MM') : 'Cleared';
    let extInfo = `Month Picker - ${selectedValue}`;

    if (activeSegment === 'studio') {
      extInfo = `${extInfo} - Studio ID: ${currentStudioId || 'Unknown'}`;
    } else if (activeSegment === 'projects') {
      extInfo = `${extInfo} - Project ID: ${currentProjectId || 'Unknown'}`;
    }

    const btnPoint = createBtnPoint(tabConfig, 'monthPicker', extInfo);
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }
  };

  // Disable months outside October 2025 - max data time from API
  // Return false for disabled dates, true for enabled dates
  const disabledMonth = (date: any) => {
    // If we don't have maxDataTime yet, disable all months
    if (!maxDataTime) {
      return false;
    }

    const minDate = moment(GLOBAL_MIN_DATE, 'YYYY-MM'); // 2025-10
    const maxDate = moment(maxDataTime, 'YYYY-MM');
    // const maxDate = "2026-11";

    // Return true if date is within range (enabled), false otherwise (disabled)
    return !date.isBefore(minDate, 'month') && !date.isAfter(maxDate, 'month');
  };

  // Get the current tab's selected date
  const currentSelectedMonth = tabMonthSelections[activeSegment];

  // Date picker form configuration
  const dateFormList = [
    {
      key: 'date',
      render: () => (
        <DatePickerWrapper className="dbs-month-picker-with-tooltip" label={formatMessage({ id: 'dbs_month' })}>
          <MonthPicker
            overlayClassName="dbs-month-picker-overlay"
            value={currentSelectedMonth}
            onChange={handleDateChange}
            disabledMonth={disabledMonth}
            placeholder="Please Select"
            clearable
            disabled={isEditing}
          />
        </DatePickerWrapper>
      ),
    },
  ];

  const [dataAnalyticsPage, setDataAnalyticsPage] = useState(false);
  const [dataAnalyticsPageIndev, setDataAnalyticsPageIndev] = useState(false);

  const hasLiveTitlesView = useMemo(
    () => tabList.find((item) => item.value === 'titles')?.permission?.view ?? false,
    [tabList],
  );

  const liveViewSegmentOptions = useMemo(
    () => [
      {
        value: 'business',
        text: (
          <span className="dbs-live-view-segment__option">
            <span className="dbs-live-view-segment__icon dbs-live-view-segment__icon--business" aria-hidden />
            Business
          </span>
        ),
      },
      {
        value: 'finance',
        text: (
          <span className="dbs-live-view-segment__option">
            <span className="dbs-live-view-segment__icon dbs-live-view-segment__icon--finance" aria-hidden />
            Finance
          </span>
        ),
      },
    ],
    [],
  );

  const handleLiveViewChange = (value: string) => {
    const nextLiveView = value as TLiveView;
    if (nextLiveView === activeLiveView) {
      return;
    }

    const segmentLabel = nextLiveView === 'finance' ? 'Finance' : 'Business';
    const btnPoint = createBtnPoint(LiveTitlesTab, 'liveViewSegment', `Live View - ${segmentLabel}`);
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }

    if (nextLiveView === 'finance') {
      setDataAnalyticsPage(false);
      history.push(buildLiveTitlesUrl({ liveView: 'finance', gameTitle: 'all' }));
      return;
    }
    history.push(
      buildLiveTitlesUrl({
        liveView: 'business',
        gameTitle: activeGameTitle || 'all',
      }),
    );
  };

  const goToDataAnalytics = () => {
    // Track buried point for Data Analytics button
    const btnPoint = createBtnPoint(LiveTitlesTab, 'dataAnalytics', 'Data Analytics');
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }
    setDataAnalyticsPage(true);
  };

  const goToDataAnalyticsIndev = () => {
    // Track buried point for Data Analytics button
    const btnPoint = createBtnPoint(InDevProjectsTab, 'dataAnalytics', 'Data Analytics');
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }
    setDataAnalyticsPageIndev(true);
  };

  const init = () => {
    const newTab = getFormattedTabList(optionsSegment);
    setTabList(newTab);
  };

  useEffect(() => {
    init();
  }, [optionsSegment, permissionReady]); // Re-run when optionsSegment or permissions change

  const activeTabHasView = useMemo(
    () => tabList.find((item) => item.value === activeSegment)?.hasAuth,
    [tabList, activeSegment],
  );

  // Unlike the other management tabs, Board Governance is hidden when unreadable.
  const visibleTabList = useMemo(
    () => tabList.filter((item) => item.key !== 'studio_boardGovernance' || item.hasAuth),
    [tabList],
  );

  const shouldShowGlobalMonthPicker =
    activeTabHasView &&
    activeSegment !== 'titles' &&
    activeSegment !== 'boardGovernance' &&
    !(activeSegment === 'projects' && !currentProjectId);

  return (
    <>
      {dataAnalyticsPageIndev ? (
        <InDevDownload setDataAnalyticsPage={setDataAnalyticsPageIndev} />
      ) : dataAnalyticsPage ? (
        <LiveDownload setDataAnalyticsPage={setDataAnalyticsPage} />
      ) : (
        <>
          <div className="dbs-segment-row">
            <div className="dbs-segment-row__left">
              <Segment
                className="dbs-gradient-segment"
                options={visibleTabList}
                value={activeSegment}
                onChange={handleActiveSegmentChange}
              />

              {activeSegment === 'boardGovernance' && activeTabHasView && (
                <SearchableSelectMultipleInherit
                  className="dbs-board-governance-studio-filter"
                  boxClassName="dbs-metric-select-overlay"
                  label="Studio"
                  options={boardGovernanceSelectOptions}
                  groups={boardGovernanceSelectGroups}
                  value={boardGovernanceStudioIds}
                  onChange={handleBoardGovernanceStudioChange}
                  onClose={handleBoardGovernanceStudioClose}
                  button={boardGovernanceSelectButton}
                  // Hide the default "All" checkbox; use group-header Select All / Clear All instead.
                  allOption={false}
                  required={false}
                  showPlaceholder
                  placeholder="Please select"
                  searchable
                  searchPlaceholder="Search"
                  disabled={boardGovernanceOptionsLoading || isEditing}
                />
              )}

              {activeSegment === 'titles' && !dataAnalyticsPage && hasLiveTitlesView && (
                <>
                  <span className="dbs-live-view-segment__divider">-</span>
                  <Segment
                    className="dbs-live-view-segment dbs-gradient-segment"
                    options={liveViewSegmentOptions}
                    value={activeLiveView}
                    onChange={handleLiveViewChange}
                  />
                </>
              )}

              {/* Date Picker - hide on InDev ALL (child page has its own month picker) */}
              {shouldShowGlobalMonthPicker && <TableForm formList={dateFormList} />}
            </div>

            {/* Dynamic button depending on active tab */}
            {activeTabHasView &&
              (() => {
                switch (activeSegment) {
                  case 'titles': {
                    if (dataAnalyticsPage || activeLiveView === 'finance') {
                      return null;
                    }
                    const titlesTab = tabList.find((item) => item.value === 'titles');
                    return (
                      <>
                        {titlesTab?.hasAuth ? (
                          <Button className="dbs-gradient-btn" onClick={goToDataAnalytics}>
                            {formatMessage({ id: 'dbs_button_data_analytics' })}
                            <img src={arrowRightWhite} alt="Arrow Right" />
                          </Button>
                        ) : null}
                      </>
                    );
                  }

                  case 'projects':
                    // 4.0 GR Management button at In Dev Projects tab
                    if (dataAnalyticsPageIndev) {
                      return null;
                    }
                    const titlesTab = tabList.find((item) => item.value === 'projects');
                    return (
                      <>
                        {titlesTab?.hasAuth ? (
                          <Button className="dbs-gradient-btn" onClick={goToDataAnalyticsIndev}>
                            {formatMessage({ id: 'dbs_button_data_analytics' })}
                            <img src={arrowRightWhite} alt="Arrow Right" />
                          </Button>
                        ) : null}
                      </>
                    );

                  default:
                    return null;
                }
              })()}
          </div>
          {tabList.map((item) =>
            activeSegment === item.value ? (
              item.hasAuth ? (
                item.modules
              ) : (
                <NoAuthPage admins={item.admins || []} />
              )
            ) : null,
          )}
        </>
      )}
    </>
  );
}
